import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './App.css';

const API_URL = 'https://verifiedprompts-backend.onrender.com';

// Axios configuratie
const api = axios.create({
  baseURL: API_URL
});

// Token toevoegen aan requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// =====================
// COMPONENTS
// =====================

function Navbar({ user, setUser }) {
  const navigate = useNavigate();

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <nav className="navbar">
      <Link to="/" className="logo">✓ VerifiedPrompts</Link>
      <div className="nav-links">
        <Link to="/">Marketplace</Link>
        <Link to="/collections">Collections</Link>
        <Link to="/bundles">Bundles</Link>
        {user ? (
          <>
            <Link to="/my-purchases">My Purchases</Link>
            <Link to="/favorites">Favorites</Link>
            <Link to="/seller-dashboard">Seller Dashboard</Link>
            <Link to="/affiliate">Affiliate</Link>
            <Link to="/sell">Sell</Link>
            <span className="user-name">Hi, {user.name || user.email}</span>
            <button onClick={logout} className="btn-logout">Logout</button>
          </>
        ) : (
          <>
            <Link to="/login">Login</Link>
            <Link to="/register" className="btn-register">Sign Up</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-links">
        <a href="/about.html">About</a>
        <a href="/privacy-policy.html">Privacy Policy</a>
        <a href="/terms-of-service.html">Terms of Service</a>
      </div>
      <div className="footer-info">
        <p>VerifiedPrompts · KvK 99250926 · Almere, The Netherlands</p>
        <p>© 2024 VerifiedPrompts. All rights reserved.</p>
      </div>
    </footer>
  );
}

// =====================
// HOME PAGE WITH TRENDING
// =====================

function Home({ user }) {
  const [prompts, setPrompts] = useState([]);
  const [trendingPrompts, setTrendingPrompts] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', aiModel: '' });
  const [sortBy, setSortBy] = useState('score-desc');

  useEffect(() => {
    loadPrompts();
    loadTrending();
    loadTopSellers();
  }, [filter]);

  const loadPrompts = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.aiModel) params.append('aiModel', filter.aiModel);

      const response = await api.get(`/prompts?${params}`);
      setPrompts(response.data);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrending = async () => {
    try {
      const response = await api.get('/prompts/trending?limit=4');
      setTrendingPrompts(response.data);
    } catch (error) {
      console.error('Error loading trending:', error);
    }
  };

  const loadTopSellers = async () => {
    try {
      const response = await api.get('/sellers/top?limit=3');
      setTopSellers(response.data);
    } catch (error) {
      console.error('Error loading top sellers:', error);
    }
  };

  // Sorteer functie
  const sortedPrompts = [...prompts].sort((a, b) => {
    switch (sortBy) {
      case 'score-desc':
        return (b.qualityScore || 0) - (a.qualityScore || 0);
      case 'score-asc':
        return (a.qualityScore || 0) - (b.qualityScore || 0);
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'oldest':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'name-asc':
        return a.title.localeCompare(b.title);
      case 'name-desc':
        return b.title.localeCompare(a.title);
      case 'popular':
        return (b.purchaseCount || 0) - (a.purchaseCount || 0);
      default:
        return 0;
    }
  });

  return (
    <div className="home">
      <header className="hero">
        <h1>AI-Verified Prompts</h1>
        <p>The only marketplace where every prompt is tested and verified by AI</p>
      </header>

      {/* Trending Section */}
      {trendingPrompts.length > 0 && (
        <section className="trending-section">
          <h2>🔥 Trending Prompts</h2>
          <div className="trending-grid">
            {trendingPrompts.map(prompt => (
              <PromptCard key={prompt.id} prompt={prompt} user={user} compact />
            ))}
          </div>
        </section>
      )}

      {/* Top Sellers Section */}
      {topSellers.length > 0 && (
        <section className="top-sellers-section">
          <h2>⭐ Top Sellers</h2>
          <div className="top-sellers-grid">
            {topSellers.map(seller => (
              <Link to={`/seller/${seller.id}`} key={seller.id} className="seller-card">
                <div className="seller-avatar">
                  {seller.avatarUrl ? (
                    <img src={seller.avatarUrl} alt={seller.name} />
                  ) : (
                    <span>{seller.name?.charAt(0) || '?'}</span>
                  )}
                </div>
                <div className="seller-info">
                  <h3>{seller.name}</h3>
                  {seller.isVerifiedSeller && <span className="verified-seller-badge">✓ Verified</span>}
                  <p>{seller.promptCount} prompts · {seller.totalSales} sales</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="filters">
        <select value={filter.category} onChange={(e) => setFilter({...filter, category: e.target.value})}>
          <option value="">All Categories</option>
          <option value="business">Business</option>
          <option value="creative">Creative</option>
          <option value="code">Code</option>
          <option value="marketing">Marketing</option>
          <option value="image">Image</option>
        </select>

        <select value={filter.aiModel} onChange={(e) => setFilter({...filter, aiModel: e.target.value})}>
          <option value="">All AI Models</option>
          <option value="chatgpt">ChatGPT</option>
          <option value="claude">Claude</option>
          <option value="gemini">Gemini</option>
          <option value="midjourney">Midjourney</option>
        </select>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
          <option value="score-desc">Highest Score</option>
          <option value="score-asc">Lowest Score</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="name-asc">Name: A-Z</option>
          <option value="name-desc">Name: Z-A</option>
          <option value="popular">Most Popular</option>
        </select>
      </div>

      {loading ? (
        <p className="loading">Loading...</p>
      ) : (
        <div className="prompts-grid">
          {sortedPrompts.length === 0 ? (
            <p>No prompts found</p>
          ) : (
            sortedPrompts.map(prompt => (
              <PromptCard key={prompt.id} prompt={prompt} user={user} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// =====================
// PROMPT CARD WITH FAVORITES
// =====================

function PromptCard({ prompt, user, compact }) {
  const [isFavorite, setIsFavorite] = useState(prompt.isFavorite || false);
  const [favCount, setFavCount] = useState(prompt.favoritesCount || 0);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      alert('Please login to add favorites');
      return;
    }

    try {
      if (isFavorite) {
        await api.delete(`/prompts/${prompt.id}/favorite`);
        setIsFavorite(false);
        setFavCount(prev => Math.max(0, prev - 1));
      } else {
        await api.post(`/prompts/${prompt.id}/favorite`);
        setIsFavorite(true);
        setFavCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  return (
    <Link to={`/prompt/${prompt.id}`} className={`prompt-card ${compact ? 'compact' : ''}`}>
      <div className="prompt-header">
        <span className="prompt-category">{prompt.category}</span>
        <span className="prompt-model">{prompt.aiModel}</span>
        <button
          className={`favorite-btn ${isFavorite ? 'active' : ''}`}
          onClick={toggleFavorite}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? '❤️' : '🤍'} {favCount > 0 && <span>{favCount}</span>}
        </button>
      </div>
      <h3>{prompt.title}</h3>
      <p>{prompt.description}</p>
      <div className="prompt-footer">
        <div className="prompt-scores">
          {prompt.isVerified && (
            <span className="verified-badge">✓ Verified</span>
          )}
          {prompt.qualityScore && (
            <span className="quality-score">Score: {prompt.qualityScore}/10</span>
          )}
          {prompt.hasGuarantee && (
            <span className="guarantee-badge">💰 {prompt.guaranteeDays || 7}-day guarantee</span>
          )}
        </div>
        <span className="prompt-price">${prompt.price.toFixed(2)}</span>
      </div>
    </Link>
  );
}

// =====================
// PROMPT DETAIL WITH DEMO & Q&A
// =====================

function PromptDetail({ user }) {
  const { id } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('details');

  // Demo state
  const [demoInput, setDemoInput] = useState('');
  const [demoOutput, setDemoOutput] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null);

  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [askingQuestion, setAskingQuestion] = useState(false);

  // Favorite state
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadPrompt();
    loadQuestions();
    loadDemoInfo();
  }, [id]);

  const loadPrompt = async () => {
    try {
      const response = await api.get(`/prompts/${id}/full`);
      setPrompt(response.data);
      setIsFavorite(response.data.isFavorite || false);
    } catch (error) {
      try {
        const response = await api.get(`/prompts/${id}`);
        setPrompt(response.data);
      } catch (err) {
        setMessage('Prompt not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await api.get(`/prompts/${id}/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const loadDemoInfo = async () => {
    try {
      const response = await api.get(`/prompts/${id}/demo-info`);
      setDemoInfo(response.data);
    } catch (error) {
      console.error('Error loading demo info:', error);
    }
  };

  const runDemo = async () => {
    if (!user) {
      setMessage('Please login to try the demo');
      return;
    }

    if (!demoInput.trim()) {
      setMessage('Please enter test input for the demo');
      return;
    }

    setDemoLoading(true);
    setDemoOutput('');
    setMessage('');

    try {
      const response = await api.post(`/prompts/${id}/demo`, {
        testInput: demoInput
      });
      setDemoOutput(response.data.output);
      setDemoInfo(prev => ({
        ...prev,
        remainingDemos: response.data.remainingDemos
      }));
    } catch (error) {
      setMessage(error.response?.data?.error || 'Demo failed');
    } finally {
      setDemoLoading(false);
    }
  };

  const askQuestion = async () => {
    if (!user) {
      setMessage('Please login to ask a question');
      return;
    }

    if (!newQuestion.trim()) return;

    setAskingQuestion(true);
    try {
      const response = await api.post(`/prompts/${id}/questions`, {
        question: newQuestion
      });
      setQuestions([response.data, ...questions]);
      setNewQuestion('');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to post question');
    } finally {
      setAskingQuestion(false);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      setMessage('Please login to add favorites');
      return;
    }

    try {
      if (isFavorite) {
        await api.delete(`/prompts/${id}/favorite`);
        setIsFavorite(false);
      } else {
        await api.post(`/prompts/${id}/favorite`);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const purchase = async () => {
    if (!user) {
      setMessage('Please login to purchase');
      return;
    }

    setPurchasing(true);
    try {
      const response = await api.post(`/prompts/${id}/checkout`);
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      setMessage(error.response?.data?.error || 'Something went wrong');
      setPurchasing(false);
    }
  };

  const requestRefund = async () => {
    if (!window.confirm('Are you sure you want to request a refund for this prompt?')) {
      return;
    }

    try {
      await api.post(`/prompts/${id}/refund`, {
        reason: 'Refund requested via prompt detail page'
      });
      setMessage('Refund request submitted successfully!');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to request refund');
    }
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!prompt) return <p className="error">{message}</p>;

  return (
    <div className="prompt-detail">
      <div className="prompt-detail-header">
        <span className="prompt-category">{prompt.category}</span>
        <span className="prompt-model">{prompt.aiModel}</span>
        {prompt.isVerified && <span className="verified-badge">✓ AI Verified</span>}
        {prompt.hasGuarantee && (
          <span className="guarantee-badge">💰 {prompt.guaranteeDays || 7}-day money-back guarantee</span>
        )}
        <button
          className={`favorite-btn-large ${isFavorite ? 'active' : ''}`}
          onClick={toggleFavorite}
        >
          {isFavorite ? '❤️ Saved' : '🤍 Save'}
        </button>
      </div>

      <h1>{prompt.title}</h1>
      <p className="prompt-description">{prompt.description}</p>

      {/* Stats Bar */}
      <div className="prompt-stats-bar">
        {prompt.difficulty && (
          <span className="stat-item">
            <strong>Difficulty:</strong> {prompt.difficulty}
          </span>
        )}
        {prompt.estimatedTime && (
          <span className="stat-item">
            <strong>Time saved:</strong> ~{prompt.estimatedTime} min
          </span>
        )}
        {prompt.salesCount > 0 && (
          <span className="stat-item">
            <strong>Sales:</strong> {prompt.salesCount}
          </span>
        )}
        {prompt.favoritesCount > 0 && (
          <span className="stat-item">
            <strong>Favorites:</strong> {prompt.favoritesCount}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={activeTab === 'details' ? 'active' : ''}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={activeTab === 'demo' ? 'active' : ''}
          onClick={() => setActiveTab('demo')}
        >
          Try Demo {demoInfo && `(${demoInfo.remainingDemos} left)`}
        </button>
        <button
          className={activeTab === 'qa' ? 'active' : ''}
          onClick={() => setActiveTab('qa')}
        >
          Q&A ({questions.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'details' && (
          <>
            <div className="prompt-scores-detail">
              {prompt.verificationScore && (
                <div className="score-item">
                  <span className="score-label">Quality</span>
                  <span className="score-value">{prompt.verificationScore}/10</span>
                </div>
              )}
              {prompt.consistencyScore && (
                <div className="score-item">
                  <span className="score-label">Consistency</span>
                  <span className="score-value">{prompt.consistencyScore}/10</span>
                </div>
              )}
              {prompt.qualityScore && (
                <div className="score-item">
                  <span className="score-label">Overall</span>
                  <span className="score-value">{prompt.qualityScore}/10</span>
                </div>
              )}
            </div>

            {/* Proof Section */}
            {(prompt.targetAudience || prompt.useCase || prompt.recommendedModel || prompt.exampleOutput) && (
              <div className="proof-section">
                <h2>📋 Why This Prompt Works</h2>

                {prompt.targetAudience && (
                  <div className="proof-item">
                    <h3>👥 Perfect For</h3>
                    <p>{prompt.targetAudience}</p>
                  </div>
                )}

                {prompt.useCase && (
                  <div className="proof-item">
                    <h3>🎯 Best Used When</h3>
                    <p>{prompt.useCase}</p>
                  </div>
                )}

                {prompt.recommendedModel && (
                  <div className="proof-item">
                    <h3>🤖 Recommended AI Model</h3>
                    <p>{prompt.recommendedModel}</p>
                  </div>
                )}

                {prompt.exampleOutput && (
                  <div className="proof-item example-output">
                    <h3>✨ Example Output</h3>
                    <div className="example-output-content markdown-content">
                      <ReactMarkdown>{prompt.exampleOutput}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {prompt.usageTips && (
                  <div className="proof-item usage-tips">
                    <h3>💡 Pro Tips</h3>
                    <div className="markdown-content">
                      <ReactMarkdown>{prompt.usageTips}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'demo' && (
          <div className="demo-section">
            <h3>🧪 Try Before You Buy</h3>
            <p className="demo-description">
              Test this prompt with your own input. You have {demoInfo?.remainingDemos || 0} demo tries remaining.
            </p>

            {demoInfo?.remainingDemos > 0 ? (
              <>
                <textarea
                  className="demo-input"
                  placeholder="Enter your test input here... (e.g., topic, keywords, context)"
                  value={demoInput}
                  onChange={(e) => setDemoInput(e.target.value)}
                  rows={4}
                />
                <button
                  onClick={runDemo}
                  disabled={demoLoading || !demoInput.trim()}
                  className="btn-demo"
                >
                  {demoLoading ? 'Running...' : 'Run Demo'}
                </button>

                {demoOutput && (
                  <div className="demo-output">
                    <h4>Output Preview:</h4>
                    <div className="demo-output-content markdown-content">
                      <ReactMarkdown>{demoOutput}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="demo-exhausted">
                <p>You've used all your demo tries for this prompt.</p>
                <p>Purchase to get unlimited access!</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'qa' && (
          <div className="qa-section">
            <h3>❓ Questions & Answers</h3>

            <div className="ask-question">
              <textarea
                placeholder="Ask the seller a question about this prompt..."
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                rows={3}
              />
              <button
                onClick={askQuestion}
                disabled={askingQuestion || !newQuestion.trim()}
              >
                {askingQuestion ? 'Posting...' : 'Ask Question'}
              </button>
            </div>

            <div className="questions-list">
              {questions.length === 0 ? (
                <p className="no-questions">No questions yet. Be the first to ask!</p>
              ) : (
                questions.map(q => (
                  <div key={q.id} className="question-item">
                    <div className="question-text">
                      <strong>Q:</strong> {q.question}
                    </div>
                    <div className="question-meta">
                      Asked by {q.asker?.name || 'Anonymous'} · {new Date(q.createdAt).toLocaleDateString()}
                    </div>
                    {q.answer ? (
                      <div className="answer-text">
                        <strong>A:</strong> {q.answer}
                        <div className="answer-meta">
                          Answered {new Date(q.answeredAt).toLocaleDateString()}
                        </div>
                      </div>
                    ) : (
                      <div className="awaiting-answer">Awaiting seller response...</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Seller Info */}
      <Link to={`/seller/${prompt.seller?.id}`} className="prompt-seller-card">
        <div className="seller-avatar-small">
          {prompt.seller?.avatarUrl ? (
            <img src={prompt.seller.avatarUrl} alt={prompt.seller.name} />
          ) : (
            <span>{prompt.seller?.name?.charAt(0) || '?'}</span>
          )}
        </div>
        <div className="seller-details">
          <span className="seller-name">
            {prompt.seller?.name || 'Anonymous'}
            {prompt.seller?.isVerifiedSeller && <span className="verified-seller-badge">✓</span>}
          </span>
          <span className="seller-link">View Profile →</span>
        </div>
      </Link>

      {/* Purchase Section */}
      <div className="prompt-purchase">
        <span className="prompt-price-big">${prompt.price.toFixed(2)}</span>
        {prompt.hasAccess ? (
          <>
            <div className="prompt-text-box">
              <h4>📝 Your Prompt:</h4>
              <pre>{prompt.promptText}</pre>
            </div>
            {prompt.hasGuarantee && prompt.canRequestRefund && (
              <button onClick={requestRefund} className="btn-refund">
                Request Refund
              </button>
            )}
          </>
        ) : (
          <button
            onClick={purchase}
            disabled={purchasing}
            className="btn-buy"
          >
            {purchasing ? 'Processing...' : 'Buy Now'}
          </button>
        )}
        {message && <p className="message">{message}</p>}
      </div>
    </div>
  );
}

// =====================
// SELLER PROFILE PAGE
// =====================

function SellerProfile() {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSeller();
  }, [id]);

  const loadSeller = async () => {
    try {
      const [profileRes, promptsRes] = await Promise.all([
        api.get(`/sellers/${id}/profile`),
        api.get(`/prompts?sellerId=${id}`)
      ]);
      setSeller(profileRes.data);
      setPrompts(promptsRes.data || []);
    } catch (error) {
      console.error('Error loading seller:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!seller) return <p className="error">Seller not found</p>;

  return (
    <div className="seller-profile-page">
      <div className="seller-profile-header">
        <div className="seller-avatar-large">
          {seller.avatarUrl ? (
            <img src={seller.avatarUrl} alt={seller.name} />
          ) : (
            <span>{seller.name?.charAt(0) || '?'}</span>
          )}
        </div>
        <div className="seller-profile-info">
          <h1>
            {seller.name}
            {seller.isVerifiedSeller && <span className="verified-seller-badge">✓ Verified Seller</span>}
          </h1>
          {seller.bio && <p className="seller-bio">{seller.bio}</p>}

          {seller.expertise && seller.expertise.length > 0 && (
            <div className="seller-expertise">
              {seller.expertise.map((exp, i) => (
                <span key={i} className="expertise-tag">{exp}</span>
              ))}
            </div>
          )}

          <div className="seller-links">
            {seller.website && (
              <a href={seller.website} target="_blank" rel="noopener noreferrer">🌐 Website</a>
            )}
            {seller.twitter && (
              <a href={`https://twitter.com/${seller.twitter}`} target="_blank" rel="noopener noreferrer">𝕏 Twitter</a>
            )}
            {seller.linkedin && (
              <a href={seller.linkedin} target="_blank" rel="noopener noreferrer">💼 LinkedIn</a>
            )}
          </div>

          <div className="seller-stats">
            <div className="stat">
              <span className="stat-value">{seller.promptCount || 0}</span>
              <span className="stat-label">Prompts</span>
            </div>
            <div className="stat">
              <span className="stat-value">{seller.totalSales || 0}</span>
              <span className="stat-label">Sales</span>
            </div>
            <div className="stat">
              <span className="stat-value">{seller.averageRating?.toFixed(1) || '-'}</span>
              <span className="stat-label">Rating</span>
            </div>
          </div>
        </div>
      </div>

      <h2>Prompts by {seller.name}</h2>
      <div className="prompts-grid">
        {prompts.length === 0 ? (
          <p>No prompts yet</p>
        ) : (
          prompts.map(prompt => (
            <PromptCard key={prompt.id} prompt={prompt} />
          ))
        )}
      </div>
    </div>
  );
}

// =====================
// MY FAVORITES PAGE
// =====================

function Favorites({ user }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]);

  const loadFavorites = async () => {
    try {
      const response = await api.get('/my/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="auth-form">
        <h2>My Favorites</h2>
        <p>Please <Link to="/login">login</Link> to view your favorites.</p>
      </div>
    );
  }

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="favorites-page">
      <h2>❤️ My Favorites</h2>
      {favorites.length === 0 ? (
        <p>You haven't saved any prompts yet. <Link to="/">Browse the marketplace</Link></p>
      ) : (
        <div className="prompts-grid">
          {favorites.map(fav => (
            <PromptCard key={fav.prompt.id} prompt={fav.prompt} user={user} />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================
// BUNDLES PAGE
// =====================

function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBundles();
  }, []);

  const loadBundles = async () => {
    try {
      const response = await api.get('/bundles');
      setBundles(response.data);
    } catch (error) {
      console.error('Error loading bundles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="bundles-page">
      <header className="page-header">
        <h1>📦 Prompt Bundles</h1>
        <p>Save money with curated prompt collections</p>
      </header>

      {bundles.length === 0 ? (
        <p className="no-items">No bundles available yet.</p>
      ) : (
        <div className="bundles-grid">
          {bundles.map(bundle => (
            <Link to={`/bundle/${bundle.id}`} key={bundle.id} className="bundle-card">
              <h3>{bundle.name}</h3>
              <p>{bundle.description}</p>
              <div className="bundle-meta">
                <span className="bundle-count">{bundle.promptCount} prompts</span>
                <span className="bundle-discount">Save {bundle.discountPercent}%</span>
              </div>
              <div className="bundle-price">
                <span className="original-price">${bundle.originalPrice?.toFixed(2)}</span>
                <span className="sale-price">${bundle.price.toFixed(2)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================
// BUNDLE DETAIL PAGE
// =====================

function BundleDetail({ user }) {
  const { id } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadBundle();
  }, [id]);

  const loadBundle = async () => {
    try {
      const response = await api.get(`/bundles/${id}`);
      setBundle(response.data);
    } catch (error) {
      console.error('Error loading bundle:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseBundle = async () => {
    if (!user) {
      setMessage('Please login to purchase');
      return;
    }

    setPurchasing(true);
    try {
      const response = await api.post(`/bundles/${id}/checkout`);
      window.location.href = response.data.checkoutUrl;
    } catch (error) {
      setMessage(error.response?.data?.error || 'Something went wrong');
      setPurchasing(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!bundle) return <p className="error">Bundle not found</p>;

  return (
    <div className="bundle-detail">
      <h1>{bundle.name}</h1>
      <p className="bundle-description">{bundle.description}</p>

      <div className="bundle-savings">
        <span className="discount-badge">Save {bundle.discountPercent}%</span>
        <div className="bundle-prices">
          <span className="original">Originally ${bundle.originalPrice?.toFixed(2)}</span>
          <span className="current">${bundle.price.toFixed(2)}</span>
        </div>
      </div>

      <h2>Included Prompts ({bundle.items?.length || 0})</h2>
      <div className="bundle-items">
        {bundle.items?.map(item => (
          <Link to={`/prompt/${item.prompt.id}`} key={item.id} className="bundle-item">
            <h4>{item.prompt.title}</h4>
            <p>{item.prompt.description}</p>
            <span className="item-price">${item.prompt.price.toFixed(2)}</span>
          </Link>
        ))}
      </div>

      <div className="bundle-purchase">
        <button
          onClick={purchaseBundle}
          disabled={purchasing}
          className="btn-buy"
        >
          {purchasing ? 'Processing...' : `Buy Bundle - $${bundle.price.toFixed(2)}`}
        </button>
        {message && <p className="message">{message}</p>}
      </div>
    </div>
  );
}

// =====================
// COLLECTIONS PAGE
// =====================

function Collections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const response = await api.get('/collections');
      setCollections(response.data);
    } catch (error) {
      console.error('Error loading collections:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="collections-page">
      <header className="page-header">
        <h1>🎯 Curated Collections</h1>
        <p>Hand-picked prompts by our team</p>
      </header>

      {collections.length === 0 ? (
        <p className="no-items">No collections available yet.</p>
      ) : (
        <div className="collections-grid">
          {collections.map(collection => (
            <Link to={`/collection/${collection.slug}`} key={collection.id} className="collection-card">
              <h3>{collection.name}</h3>
              <p>{collection.description}</p>
              <span className="collection-count">{collection.promptCount} prompts</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================
// COLLECTION DETAIL PAGE
// =====================

function CollectionDetail({ user }) {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollection();
  }, [slug]);

  const loadCollection = async () => {
    try {
      const response = await api.get(`/collections/${slug}`);
      setCollection(response.data);
    } catch (error) {
      console.error('Error loading collection:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!collection) return <p className="error">Collection not found</p>;

  return (
    <div className="collection-detail">
      <header className="collection-header">
        <h1>{collection.name}</h1>
        <p>{collection.description}</p>
      </header>

      <div className="prompts-grid">
        {collection.items?.map(item => (
          <PromptCard key={item.prompt.id} prompt={item.prompt} user={user} />
        ))}
      </div>
    </div>
  );
}

// =====================
// AUTH COMPONENTS
// =====================

function Login({ setUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/login', { email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  return (
    <div className="auth-form">
      <h2>Login</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
        {error && <p className="error">{error}</p>}
      </form>
      <p>Don't have an account? <Link to="/register">Sign up here</Link></p>
    </div>
  );
}

function Register({ setUser }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/register', { name, email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    }
  };

  return (
    <div className="auth-form">
      <h2>Sign Up</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Sign Up</button>
        {error && <p className="error">{error}</p>}
      </form>
      <p>Already have an account? <Link to="/login">Login here</Link></p>
    </div>
  );
}

// =====================
// SELL PROMPT
// =====================

function Sell() {
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    category: 'business',
    aiModel: 'chatgpt',
    promptText: '',
    difficulty: 'intermediate',
    estimatedTime: '',
    hasGuarantee: true,
    guaranteeDays: 7
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/prompts', form);
      setMessage(`Prompt created! ID: ${response.data.prompt.id}. AI verification in progress...`);

      await api.post(`/prompts/${response.data.prompt.id}/verify`);
      setMessage('Prompt created and verified! Redirecting...');

      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sell-form">
      <h2>Sell a Prompt</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm({...form, title: e.target.value})}
          required
        />
        <textarea
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
        />
        <input
          type="number"
          step="0.01"
          placeholder="Price ($)"
          value={form.price}
          onChange={(e) => setForm({...form, price: e.target.value})}
          required
        />

        <div className="form-row">
          <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
            <option value="business">Business</option>
            <option value="creative">Creative</option>
            <option value="code">Code</option>
            <option value="marketing">Marketing</option>
            <option value="image">Image</option>
          </select>
          <select value={form.aiModel} onChange={(e) => setForm({...form, aiModel: e.target.value})}>
            <option value="chatgpt">ChatGPT</option>
            <option value="claude">Claude</option>
            <option value="gemini">Gemini</option>
            <option value="midjourney">Midjourney</option>
          </select>
        </div>

        <div className="form-row">
          <select value={form.difficulty} onChange={(e) => setForm({...form, difficulty: e.target.value})}>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <input
            type="number"
            placeholder="Est. time saved (min)"
            value={form.estimatedTime}
            onChange={(e) => setForm({...form, estimatedTime: e.target.value})}
          />
        </div>

        <textarea
          placeholder="The prompt text... (use {variable} for variables)"
          value={form.promptText}
          onChange={(e) => setForm({...form, promptText: e.target.value})}
          rows="5"
          required
        />

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={form.hasGuarantee}
            onChange={(e) => setForm({...form, hasGuarantee: e.target.checked})}
          />
          <span>Offer {form.guaranteeDays}-day money-back guarantee</span>
        </label>

        <label className="terms-checkbox">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            required
          />
          <span>I agree to the <strong>70/30 revenue split</strong> (you receive 70%, VerifiedPrompts receives 30%)</span>
        </label>
        <button type="submit" disabled={loading || !agreedToTerms}>
          {loading ? 'Verifying with AI...' : 'Create Prompt'}
        </button>
        {message && <p className="message">{message}</p>}
      </form>
    </div>
  );
}

// =====================
// MY PURCHASES
// =====================

function MyPurchases() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      const response = await api.get('/my/purchases');
      setPurchases(response.data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="my-purchases">
      <h2>My Purchased Prompts</h2>
      {purchases.length === 0 ? (
        <p>You haven't purchased any prompts yet. <Link to="/">Browse the marketplace</Link></p>
      ) : (
        <div className="purchases-list">
          {purchases.map(purchase => (
            <div key={purchase.id} className="purchase-item">
              <h3>{purchase.prompt.title}</h3>
              <p>{purchase.prompt.description}</p>
              <div className="prompt-text-box">
                <h4>📝 The Prompt:</h4>
                <pre>{purchase.prompt.promptText}</pre>
              </div>
              <p className="purchase-date">
                Purchased on: {new Date(purchase.createdAt).toLocaleDateString('en-US')}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================
// PURCHASE SUCCESS
// =====================

function PurchaseSuccess() {
  const [status, setStatus] = useState('loading');
  const [promptText, setPromptText] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    confirmPurchase();
  }, []);

  const confirmPurchase = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const promptId = params.get('prompt_id');

    if (!sessionId || !promptId) {
      setStatus('error');
      setMessage('Invalid session');
      return;
    }

    try {
      const response = await api.post(`/prompts/${promptId}/confirm-purchase`, {
        sessionId
      });
      setStatus('success');
      setPromptText(response.data.promptText);
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.error || 'Something went wrong');
    }
  };

  if (status === 'loading') {
    return <p className="loading">Confirming your purchase...</p>;
  }

  if (status === 'error') {
    return (
      <div className="auth-form">
        <h2>Purchase Failed</h2>
        <p>{message}</p>
        <Link to="/">Back to Marketplace</Link>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <h2>✓ Purchase Successful!</h2>
      <p>Thank you for your purchase. Here's your prompt:</p>
      <div className="prompt-text-box">
        <pre>{promptText}</pre>
      </div>
      <p><Link to="/my-purchases">View all your purchases</Link></p>
    </div>
  );
}

// =====================
// EDIT PROMPT
// =====================

function EditPrompt() {
  const { id } = useParams();
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    category: 'business',
    aiModel: 'chatgpt',
    promptText: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadPrompt();
  }, [id]);

  const loadPrompt = async () => {
    try {
      const response = await api.get(`/prompts/${id}`);
      const prompt = response.data;

      if (!prompt.hasAccess) {
        setMessage('You do not have permission to edit this prompt.');
        setLoading(false);
        return;
      }

      setForm({
        title: prompt.title,
        description: prompt.description,
        price: prompt.price.toString(),
        category: prompt.category,
        aiModel: prompt.aiModel,
        promptText: prompt.promptText || ''
      });
    } catch (error) {
      console.error('Error loading prompt:', error);
      setMessage('Could not load prompt.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await api.put(`/prompts/${id}`, form);
      setMessage('Prompt updated successfully! Redirecting...');
      setTimeout(() => navigate('/seller-dashboard'), 1500);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not update prompt.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  if (message && !form.title) {
    return (
      <div className="auth-form">
        <h2>Edit Prompt</h2>
        <p className="error">{message}</p>
        <Link to="/seller-dashboard">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="sell-form">
      <h2>Edit Prompt</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm({...form, title: e.target.value})}
          required
        />
        <textarea
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
        />
        <input
          type="number"
          step="0.01"
          placeholder="Price ($)"
          value={form.price}
          onChange={(e) => setForm({...form, price: e.target.value})}
          required
        />
        <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
          <option value="business">Business</option>
          <option value="creative">Creative</option>
          <option value="code">Code</option>
          <option value="marketing">Marketing</option>
          <option value="image">Image</option>
        </select>
        <select value={form.aiModel} onChange={(e) => setForm({...form, aiModel: e.target.value})}>
          <option value="chatgpt">ChatGPT</option>
          <option value="claude">Claude</option>
          <option value="gemini">Gemini</option>
          <option value="midjourney">Midjourney</option>
        </select>
        <textarea
          placeholder="The prompt text... (use {variable} for variables)"
          value={form.promptText}
          onChange={(e) => setForm({...form, promptText: e.target.value})}
          rows="5"
          required
        />
        <div className="edit-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <Link to="/seller-dashboard" className="btn-cancel">Cancel</Link>
        </div>
        {message && <p className="message">{message}</p>}
      </form>
    </div>
  );
}

// =====================
// SELLER DASHBOARD
// =====================

function SellerDashboard({ user }) {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [myPrompts, setMyPrompts] = useState([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkStripeStatus();
    loadMyPrompts();
    loadUnansweredQuestions();
    loadRefundRequests();
  }, []);

  const checkStripeStatus = async () => {
    try {
      const response = await api.get('/stripe/connect/status');
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyPrompts = async () => {
    try {
      const response = await api.get('/my/prompts');
      setMyPrompts(response.data);
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const loadUnansweredQuestions = async () => {
    try {
      const response = await api.get('/my/questions/unanswered');
      setUnansweredQuestions(response.data);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const loadRefundRequests = async () => {
    try {
      const response = await api.get('/my/prompts/refunds');
      setRefundRequests(response.data);
    } catch (error) {
      console.error('Error loading refunds:', error);
    }
  };

  const deletePrompt = async (promptId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
      return;
    }

    setDeleting(promptId);
    try {
      await api.delete(`/prompts/${promptId}`);
      setMyPrompts(myPrompts.filter(p => p.id !== promptId));
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Could not delete prompt. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const response = await api.post('/stripe/connect');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      alert('Could not connect Stripe. Please try again.');
      setConnecting(false);
    }
  };

  const openDashboard = async () => {
    try {
      const response = await api.get('/stripe/connect/dashboard');
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error opening dashboard:', error);
      alert('Could not open Stripe dashboard.');
    }
  };

  const answerQuestion = async (questionId, answer) => {
    try {
      await api.put(`/questions/${questionId}/answer`, { answer });
      setUnansweredQuestions(unansweredQuestions.filter(q => q.id !== questionId));
    } catch (error) {
      console.error('Error answering question:', error);
      alert('Could not submit answer. Please try again.');
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="seller-dashboard">
      <h2>Seller Dashboard</h2>

      {/* Profile Section */}
      <div className="dashboard-section">
        <h3>👤 Your Profile</h3>
        <Link to="/edit-profile" className="btn-edit-profile">Edit Seller Profile</Link>
      </div>

      {/* Stripe Connect Section */}
      <div className="stripe-connect-section">
        <h3>💰 Payment Setup</h3>

        {!stripeStatus?.connected ? (
          <div className="connect-prompt">
            <p>Connect your Stripe account to receive payments when your prompts sell.</p>
            <p className="revenue-split">You'll earn <strong>70%</strong> of every sale!</p>
            <button
              onClick={connectStripe}
              disabled={connecting}
              className="btn-connect-stripe"
            >
              {connecting ? 'Connecting...' : 'Connect Stripe Account'}
            </button>
          </div>
        ) : !stripeStatus?.onboardingComplete ? (
          <div className="connect-prompt">
            <p className="status-pending">⏳ Stripe account connected but setup incomplete.</p>
            <p>Please complete your Stripe onboarding to receive payments.</p>
            <button
              onClick={connectStripe}
              disabled={connecting}
              className="btn-connect-stripe"
            >
              {connecting ? 'Loading...' : 'Complete Setup'}
            </button>
          </div>
        ) : (
          <div className="connect-complete">
            <p className="status-success">✅ Stripe connected! You're ready to receive payments.</p>
            <p>When someone buys your prompt, 70% goes directly to your Stripe account.</p>
            <button onClick={openDashboard} className="btn-stripe-dashboard">
              View Stripe Dashboard
            </button>
          </div>
        )}
      </div>

      {/* Unanswered Questions */}
      {unansweredQuestions.length > 0 && (
        <div className="dashboard-section questions-section">
          <h3>❓ Unanswered Questions ({unansweredQuestions.length})</h3>
          {unansweredQuestions.map(q => (
            <div key={q.id} className="question-to-answer">
              <p className="question-prompt-title">On: {q.prompt?.title}</p>
              <p className="question-text"><strong>Q:</strong> {q.question}</p>
              <p className="question-asker">From: {q.asker?.name || 'Anonymous'}</p>
              <AnswerForm questionId={q.id} onAnswer={answerQuestion} />
            </div>
          ))}
        </div>
      )}

      {/* Refund Requests */}
      {refundRequests.length > 0 && (
        <div className="dashboard-section refunds-section">
          <h3>💸 Refund Requests ({refundRequests.length})</h3>
          {refundRequests.map(r => (
            <div key={r.id} className="refund-request">
              <p><strong>{r.prompt?.title}</strong></p>
              <p>Requested by: {r.buyer?.name || 'Unknown'}</p>
              <p>Reason: {r.reason}</p>
              <p className={`refund-status ${r.status}`}>Status: {r.status}</p>
            </div>
          ))}
        </div>
      )}

      {/* My Prompts Section */}
      <div className="my-prompts-section">
        <h3>📝 My Prompts</h3>
        {myPrompts.length === 0 ? (
          <p className="no-prompts">You haven't created any prompts yet.</p>
        ) : (
          <div className="my-prompts-list">
            {myPrompts.map(prompt => (
              <div key={prompt.id} className="my-prompt-item">
                <div className="my-prompt-info">
                  <h4>{prompt.title}</h4>
                  <p className="my-prompt-meta">
                    <span className={`status-badge ${prompt.status}`}>{prompt.status}</span>
                    <span className="price">${prompt.price.toFixed(2)}</span>
                    <span className="category">{prompt.category}</span>
                  </p>
                </div>
                <div className="my-prompt-actions">
                  <Link to={`/prompt/${prompt.id}`} className="btn-view">View</Link>
                  <Link to={`/edit-prompt/${prompt.id}`} className="btn-edit">Edit</Link>
                  <button
                    onClick={() => deletePrompt(prompt.id, prompt.title)}
                    disabled={deleting === prompt.id}
                    className="btn-delete"
                  >
                    {deleting === prompt.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Link to="/sell" className="btn-create-prompt">Create New Prompt</Link>
      </div>
    </div>
  );
}

// Answer form component
function AnswerForm({ questionId, onAnswer }) {
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim()) return;

    setSubmitting(true);
    await onAnswer(questionId, answer);
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="answer-form">
      <textarea
        placeholder="Type your answer..."
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={2}
      />
      <button type="submit" disabled={submitting || !answer.trim()}>
        {submitting ? 'Submitting...' : 'Submit Answer'}
      </button>
    </form>
  );
}

// =====================
// EDIT PROFILE
// =====================

function EditProfile({ user, setUser }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    bio: '',
    expertise: '',
    website: '',
    twitter: '',
    linkedin: '',
    avatarUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/my/profile');
      const profile = response.data;
      setForm({
        name: profile.name || '',
        bio: profile.bio || '',
        expertise: profile.expertise?.join(', ') || '',
        website: profile.website || '',
        twitter: profile.twitter || '',
        linkedin: profile.linkedin || '',
        avatarUrl: profile.avatarUrl || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        ...form,
        expertise: form.expertise.split(',').map(e => e.trim()).filter(e => e)
      };

      const response = await api.put('/my/profile', payload);

      // Update local user
      const updatedUser = { ...user, name: form.name };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      setMessage('Profile updated successfully!');
      setTimeout(() => navigate('/seller-dashboard'), 1500);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="sell-form">
      <h2>Edit Seller Profile</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Display Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
        <textarea
          placeholder="Bio - Tell buyers about yourself"
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          rows={3}
        />
        <input
          type="text"
          placeholder="Expertise (comma-separated: marketing, copywriting, code)"
          value={form.expertise}
          onChange={(e) => setForm({ ...form, expertise: e.target.value })}
        />
        <input
          type="url"
          placeholder="Website URL"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
        />
        <input
          type="text"
          placeholder="Twitter username (without @)"
          value={form.twitter}
          onChange={(e) => setForm({ ...form, twitter: e.target.value })}
        />
        <input
          type="url"
          placeholder="LinkedIn profile URL"
          value={form.linkedin}
          onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
        />
        <input
          type="url"
          placeholder="Avatar image URL"
          value={form.avatarUrl}
          onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })}
        />

        <div className="edit-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          <Link to="/seller-dashboard" className="btn-cancel">Cancel</Link>
        </div>
        {message && <p className="message">{message}</p>}
      </form>
    </div>
  );
}

// =====================
// AFFILIATE DASHBOARD
// =====================

function AffiliateDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadAffiliateData();
  }, [user]);

  const loadAffiliateData = async () => {
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        api.get('/affiliate/stats'),
        api.get('/affiliate/leaderboard')
      ]);
      setStats(statsRes.data);
      setReferralCode(statsRes.data.referralCode || '');
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading affiliate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/affiliate/generate-code');
      setReferralCode(response.data.referralCode);
      setStats(prev => ({ ...prev, referralCode: response.data.referralCode }));
    } catch (error) {
      console.error('Error generating code:', error);
    } finally {
      setGenerating(false);
    }
  };

  const copyReferralLink = () => {
    const link = `https://getverifiedprompts.com?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnTwitter = () => {
    const link = `https://getverifiedprompts.com?ref=${referralCode}`;
    const text = encodeURIComponent('Check out VerifiedPrompts - the marketplace for AI-verified prompts! Use my link to get started:');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(link)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    const link = `https://getverifiedprompts.com?ref=${referralCode}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`, '_blank');
  };

  if (!user) {
    return <div className="affiliate-dashboard"><p>Please log in to access the affiliate program.</p></div>;
  }

  if (loading) {
    return <div className="affiliate-dashboard"><div className="loading">Loading affiliate data...</div></div>;
  }

  return (
    <div className="affiliate-dashboard">
      <div className="affiliate-header">
        <h1>🤝 Affiliate Program</h1>
        <p className="affiliate-subtitle">Earn 15% commission on every sale from your referrals</p>
      </div>

      {/* Stats Cards */}
      <div className="affiliate-stats">
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <span className="stat-value">${stats?.pendingPayout?.toFixed(2) || '0.00'}</span>
            <span className="stat-label">Pending Payout</span>
          </div>
        </div>
        <div className="stat-card paid">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <span className="stat-value">${stats?.paidOut?.toFixed(2) || '0.00'}</span>
            <span className="stat-label">Paid Out</span>
          </div>
        </div>
        <div className="stat-card earnings">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <span className="stat-value">${stats?.totalEarnings?.toFixed(2) || '0.00'}</span>
            <span className="stat-label">Total Earnings</span>
          </div>
        </div>
        <div className="stat-card referrals">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <span className="stat-value">{stats?.totalReferrals || 0}</span>
            <span className="stat-label">Referrals</span>
          </div>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="referral-section">
        <h2>Your Referral Link</h2>
        {referralCode ? (
          <div className="referral-link-box">
            <div className="referral-link">
              <span className="link-text">https://getverifiedprompts.com?ref={referralCode}</span>
              <button onClick={copyReferralLink} className="btn-copy">
                {copied ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>
            <div className="share-buttons">
              <button onClick={shareOnTwitter} className="btn-share twitter">
                🐦 Share on Twitter
              </button>
              <button onClick={shareOnLinkedIn} className="btn-share linkedin">
                💼 Share on LinkedIn
              </button>
            </div>
          </div>
        ) : (
          <div className="generate-code-section">
            <p>You don't have a referral code yet. Generate one to start earning!</p>
            <button onClick={generateCode} disabled={generating} className="btn-generate">
              {generating ? 'Generating...' : '🎯 Generate My Referral Code'}
            </button>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Share Your Link</h3>
            <p>Share your unique referral link with your audience, friends, or followers.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>They Sign Up</h3>
            <p>When someone clicks your link and creates an account, they become your referral.</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Earn Commission</h3>
            <p>You earn 15% of every purchase they make. Forever!</p>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="affiliate-leaderboard">
          <h2>🏆 Top Affiliates</h2>
          <div className="leaderboard-list">
            {leaderboard.map((affiliate, index) => (
              <div key={affiliate.id} className={`leaderboard-item rank-${index + 1}`}>
                <span className="rank">#{index + 1}</span>
                <span className="name">{affiliate.name || 'Anonymous'}</span>
                <span className="referrals">{affiliate.referralCount} referrals</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="affiliate-tips">
        <h2>💡 Tips to Maximize Earnings</h2>
        <ul>
          <li>Share your link on social media with a personal recommendation</li>
          <li>Write blog posts or create content about AI prompts</li>
          <li>Include your link in your email signature</li>
          <li>Create tutorial videos showing how to use prompts effectively</li>
        </ul>
      </div>
    </div>
  );
}

// =====================
// MAIN APP
// =====================

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  return (
    <Router>
      <div className="app">
        <Navbar user={user} setUser={setUser} />
        <main className="container">
          <Routes>
            <Route path="/" element={<Home user={user} />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/sell" element={<Sell />} />
            <Route path="/my-purchases" element={<MyPurchases />} />
            <Route path="/favorites" element={<Favorites user={user} />} />
            <Route path="/prompt/:id" element={<PromptDetail user={user} />} />
            <Route path="/purchase-success" element={<PurchaseSuccess />} />
            <Route path="/seller-dashboard" element={<SellerDashboard user={user} />} />
            <Route path="/edit-prompt/:id" element={<EditPrompt />} />
            <Route path="/edit-profile" element={<EditProfile user={user} setUser={setUser} />} />
            <Route path="/seller/:id" element={<SellerProfile />} />
            <Route path="/bundles" element={<Bundles />} />
            <Route path="/bundle/:id" element={<BundleDetail user={user} />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/collection/:slug" element={<CollectionDetail user={user} />} />
            <Route path="/affiliate" element={<AffiliateDashboard user={user} />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
